/**
 * OceantechSimpleSigner - Simple signature provider by Oceantech
 *
 * Đây là provider ký đơn giản của Oceantech:
 * - Ký bằng hình ảnh (image signature)
 * - Không cần USB token hay CA
 * - Lưu chữ ký trong user metadata (field7)
 */

import { BaseSignatureProvider } from '../ISignatureProvider';
import {
  SignType,
  SignProvider,
  SigningResult,
  SigningOptions,
  XmlSigningOptions,
  BatchSigningItem,
  BatchSigningResult,
  CertificateInfo,
} from '../../types';

export class OceantechSimpleSigner extends BaseSignatureProvider {
  readonly signType = SignType.IMAGE;
  readonly signProvider = SignProvider.OCEANTECH;
  readonly displayName = 'Oceantech Simple Signature';

  private signatureImage: string = '';

  constructor(signatureImage?: string) {
    super();
    this.signatureImage = signatureImage || '';
    this._isReady = true; // Always ready since it's a simple image signature
  }

  /**
   * Set chữ ký hình ảnh
   */
  setSignatureImage(imageBase64: string): void {
    this.signatureImage = imageBase64;
  }

  /**
   * Get chữ ký hình ảnh
   */
  getSignatureImage(): string {
    return this.signatureImage;
  }

  /**
   * Khởi tạo - luôn sẵn sàng
   */
  async initialize(): Promise<boolean> {
    this._isReady = true;
    return true;
  }

  /**
   * Không có certificate - ký bằng hình ảnh
   */
  async getCertificateInfo(): Promise<CertificateInfo | null> {
    // Image signature không có certificate
    return null;
  }

  /**
   * Ký PDF bằng chữ ký hình ảnh
   * Thực tế sẽ embed hình ảnh vào PDF
   */
  async signPdf(pdfData: string, options?: SigningOptions): Promise<SigningResult> {
    if (!this.signatureImage) {
      return this.createErrorResult('Chưa có chữ ký hình ảnh. Vui lòng vẽ chữ ký trước.');
    }

    try {
      // Với image signature, ta trả về PDF data kèm thông tin vị trí ký
      // Việc embed thực sự sẽ được thực hiện bởi pdfSignatureHelper
      return this.createSuccessResult(pdfData, {
        signerName: 'Oceantech Simple Signature',
        signedAt: new Date(),
      });
    } catch (error: any) {
      return this.createErrorResult(error.message);
    }
  }

  /**
   * Ký file - chỉ hỗ trợ PDF
   */
  async signFile(fileData: string, fileName: string, options?: SigningOptions): Promise<SigningResult> {
    if (!fileName.toLowerCase().endsWith('.pdf')) {
      return this.createErrorResult('Oceantech Simple Signature chỉ hỗ trợ file PDF');
    }

    return this.signPdf(fileData, options);
  }

  /**
   * Không hỗ trợ ký XML
   */
  async signXml(xmlContent: string, options?: XmlSigningOptions): Promise<SigningResult> {
    return this.createErrorResult(
      'Oceantech Simple Signature không hỗ trợ ký XML. ' +
      'Vui lòng sử dụng USB Token hoặc HSM để ký XML.'
    );
  }

  /**
   * Ký batch PDF
   */
  async signBatch(items: BatchSigningItem[], options?: SigningOptions): Promise<BatchSigningResult> {
    if (!this.signatureImage) {
      return {
        success: false,
        results: items.map((item) => ({
          fileName: item.fileName,
          error: 'Chưa có chữ ký hình ảnh',
        })),
      };
    }

    const results: BatchSigningResult['results'] = [];

    for (const item of items) {
      if (!item.fileName.toLowerCase().endsWith('.pdf')) {
        results.push({
          fileName: item.fileName,
          error: 'Chỉ hỗ trợ file PDF',
        });
        continue;
      }

      const result = await this.signPdf(item.content, options);
      results.push({
        fileName: item.fileName,
        signedData: result.signedData,
        error: result.error,
      });
    }

    return {
      success: results.every((r) => r.signedData && !r.error),
      results,
    };
  }

  /**
   * Kiểm tra có chữ ký hình ảnh chưa
   */
  hasSignatureImage(): boolean {
    return !!this.signatureImage;
  }
}
